/** The facilitator's plain words that more than one view says: points, the risk officer's action, and a round's finished state. */
import type { BoardTeamRow, RoundSummary } from "@bsim/contracts";
import { formatPp } from "../format";

/** A rate gap in points ("+1.20 points"): the screens never print the shorthand. */
export const pointsWords = (rate: number, opts: { readonly signed?: boolean } = {}): string => formatPp(rate, opts);

/**
 * One vocabulary for what the risk officer did and what the CEO did about it, on Needs-you, the band and the team card:
 * "objected" for an objection on a row, "declined" for a declined attestation, and the CEO "signed over" either. The board row carries
 * only that the CEO signed over one of them (`ceoOverrodeObjection`), so that phrase names both until the row carries the stance.
 */
export const SIGNED_OVER = "signed over the risk officer's decline or objection";
export const objectedWords = (n: number): string => `Risk officer objected${n > 1 ? ` · ${n}` : ""}`;

/** A round whose year has run: the status of each team is "complete", whatever the next round's pipeline says. */
export const roundDone = (round: Pick<RoundSummary, "state"> | null): boolean => round !== null && (round.state === "resolved" || round.state === "released");

/** Where a team stands in the round the table names; a finished round reads "Complete". */
export const stageWords = (stage: NonNullable<BoardTeamRow["pipelineStage"]>, round: Pick<RoundSummary, "state"> | null, words: Readonly<Record<NonNullable<BoardTeamRow["pipelineStage"]>, string>>): string =>
  roundDone(round) ? "Complete" : words[stage];

/** What the risk officer's review said, in the facilitator's words (the stance is the api's; the statement is theirs, quoted). */
export const STANCE_WORDS = { concur: "attested", concur_with_objections: "attested with objections", decline: "declined" } as const;

/** "just now", "3 minutes ago", "2 hours ago", "4 days ago": how long ago a person was last in the app. */
export function agoWords(iso: string, now: number): string {
  const min = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
  if (min < 1) return "just now";
  const unit = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"} ago`;
  return min < 60 ? unit(min, "minute") : min < 60 * 24 ? unit(Math.floor(min / 60), "hour") : unit(Math.floor(min / (60 * 24)), "day");
}
